import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../App';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, Zap } from 'lucide-react';
import { canAccessPath, landingPathForRole } from '../../lib/rolePermissions';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const intendedFrom = location.state?.from?.pathname || null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setLoading(true);
    try {
      const res = await adminApi.login(email, password);
      login(res.data.access_token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.name}!`);
      // Honour the "redirect back to where they came from" UX, but only if
      // the user's role can actually access that page. Otherwise route them
      // to their role-specific landing page (e.g. gate_scan → /admin/scan-pass).
      const role = res.data.user?.role;
      const target = (intendedFrom && canAccessPath(role, intendedFrom))
        ? intendedFrom
        : landingPathForRole(role);
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0A0A0A' }}>
      {/* Left Panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-[45%] p-10 relative overflow-hidden"
        style={{
          background: 'radial-gradient(1000px circle at 20% 40%, rgba(212,168,67,0.12), rgba(10,10,10,0) 60%), linear-gradient(180deg, #0A0A0A 0%, #111111 100%)',
          backgroundImage: `url('https://images.unsplash.com/photo-1549436304-c9438b164e5e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.85) 100%)' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={17} className="text-[#120F08]" />
            </div>
            <div>
              <p className="font-['EB_Garamond'] text-[#F2F2F2] text-xl font-semibold">Scratch Unleashed</p>
              <p className="text-[10px] text-[#B9B9B9] uppercase tracking-wider">Management Portal</p>
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <blockquote className="font-['EB_Garamond'] text-2xl text-[#F1E2B8] leading-snug italic mb-3">
            &ldquo;Every great night begins with the perfect entrance.&rdquo;
          </blockquote>
          <p className="text-[#B9B9B9] text-sm">Manage check-ins, cover charges, and guests with elegance.</p>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="w-full max-w-[360px]">
          {/* Mobile brand */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={14} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-xl font-semibold">Scratch Unleashed</span>
          </div>

          <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Welcome back</h1>
          <p className="text-[#B9B9B9] text-sm mb-8">Sign in to the admin portal</p>

          <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[#B9B9B9] text-xs">Email address</Label>
              <Input
                id="email"
                type="email"
                data-testid="admin-email-input"
                placeholder="you@scratchjsr.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-11"
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[#B9B9B9] text-xs">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  data-testid="admin-password-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-11 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F6F6F] hover:text-[#B9B9B9] transition-colors"
                >
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="admin-login-submit-button"
              disabled={loading}
              className="w-full btn-gold h-11 rounded-xl font-semibold text-sm mt-2"
            >
              {loading ? <div className="w-4 h-4 border-2 border-[#120F08] border-t-transparent rounded-full animate-spin" /> : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
